/* =========================================================
   Life InStyle — product-card image gallery.

   THE VISITOR DRIVES THE IMAGE, NEVER A CLOCK.

   The version this replaces cycled every card's shots on a timer — on hover on
   desktop, and by itself the moment a card scrolled into view on a phone. That
   was rejected on sight ("keeps on changing the pictures, which looks
   horrible"), and the objection is not about speed or easing: it is that the
   grid moved without being asked. So there is no interval in this file, and
   nothing here starts on its own. Every image change traces back to a gesture.

     • touch   → swipe horizontally across a card's image
     • pointer → click the arrows that fade in on hover, or a dot
     • keyboard→ Left/Right while the card's link has focus

   Same feature on both devices, expressed in the input each one actually has —
   the phone is not getting a lesser version, it is getting the better verb.

   ---------------------------------------------------------------------------
   WHY THE INPUT IS HANDLED GEOMETRICALLY INSTEAD OF WITH REAL BUTTONS

   Dawn makes the whole card clickable with `.card__heading a::after` — an
   absolutely-positioned pseudo-element at `z-index: 1` stretched over the
   entire card. `.card__media` sits at `z-index: 0` AND has a transform-induced
   stacking context above it (`.card__inner.gradient`), so *nothing painted
   inside the media box can ever receive a pointer event.* Controls placed
   there are invisible to hit-testing no matter what z-index they carry, and
   raising the media above the link would break tap-to-open-product.

   Rather than fight that cascade, the arrows and dots are painted inside the
   media box as pure decoration (`pointer-events: none`), and ONE capture-phase
   listener set on `.card-wrapper` decides what a click or a touch meant by
   comparing its coordinates against those elements' rectangles. One input
   pipeline, no stacking assumptions, and the product link keeps working
   everywhere we do not explicitly claim the event.

   ---------------------------------------------------------------------------
   WHY A SWIPE DOES NOT OPEN THE PRODUCT

   Three independent defences, because getting this wrong makes the whole grid
   feel broken and only the first of them is under our control:

   1. `touch-action: pan-y pinch-zoom` on the card (see instyle-brand.css).
      The browser is told up front that horizontal pans here are ours and
      vertical ones are its. That is also why this file never calls
      preventDefault on a touch: page scrolling is never trapped, because we
      never take it in the first place.
   2. Direction lock before commitment. Nothing happens until the finger has
      travelled AXIS_SLOP; whichever axis is larger wins, and **vertical wins
      ties** — we would rather miss a swipe than steal a scroll.
   3. An explicit click guard. A gesture that moved sets `suppressUntil`, and a
      capture-phase click handler cancels the link for a short window after.
      Most browsers already suppress the click once a touch exceeds their own
      slop, but "most browsers" is not a guarantee worth shipping.

   ---------------------------------------------------------------------------
   WHY THE DESKTOP GETS ARROWS AND NOT A HOVER EFFECT

   The obvious "same thing differently" for a mouse is to map the image to the
   pointer's position across the card. It was rejected here: a mouse crosses
   cards on its way somewhere else, so position-mapping would flip images the
   visitor never asked to see — the exact complaint, rebuilt with a different
   trigger. A click is unambiguous. Hovering a card only *offers* the controls;
   it never changes the picture.

   ---------------------------------------------------------------------------
   HOW THE CROSSFADE AVOIDS EVER SHOWING A HOLE

   Three stacked images per card: A (Dawn's original <img>, never touched, so
   its srcset and lazy-loading stay intact), then two buffers L1 and L2 which
   this file creates.

     front = 2  → load the incoming shot into L1, which L2 is covering, then
                  fade L2 OUT to reveal it.
     otherwise  → load the incoming shot into L2 and fade it IN.

   Only L2's opacity is ever animated, and whatever is underneath is always
   fully opaque, so there is no frame in which the card background shows
   through. Once L1 has been used it stays at opacity 1 forever; it is only
   ever re-sourced while L2 hides it.

   ---------------------------------------------------------------------------
   HARDENING — context/scroll-animation-rules.md, rules 5 and 6

   Rule 5, never let one media event be the only escape: the wait for an
   incoming image is released by `load`, by `error`, by an already-complete
   check, or by a hard timeout — whichever comes first.

   Rule 6, recover on `visibilitychange` AND `pageshow`: a backgrounded tab
   silently drops `load`, and a bfcache restore fires neither `load` nor
   `visibilitychange`. On return every card re-asserts the frame it believes it
   is on, without animation, and any half-finished gesture is thrown away.
   None of that is device-gated (rule 11).
   ========================================================= */
(function () {
  'use strict';

  var MAX_SHOTS = 6; // matches the cap in snippets/card-product.liquid

  var FADE_MS = 180; // keep in step with instyle-brand.css .instyle-cardgal__layer
  var LOAD_CAP_MS = 400; // rule 5: the wait for an incoming image always ends

  var AXIS_SLOP = 8; // px before a gesture is allowed to have a direction
  // Deliberately larger than AXIS_SLOP. Locking the axis early keeps the swipe
  // responsive, but it must not mean that a tap which drifted 10px sideways —
  // which is most taps, on a phone, in a moving car — stops opening the
  // product. Nothing below this counts as anything but a tap.
  var TAP_SLOP = 16;
  var COMMIT_FRACTION = 0.18; // of the card's width
  var COMMIT_MIN = 28;
  var COMMIT_MAX = 90;
  var FLICK_PX_PER_MS = 0.35; // a short, fast swipe still counts as one step
  var FLICK_WINDOW_MS = 120; // velocity is measured at release, not averaged
  var CLICK_GUARD_MS = 450; // iOS can deliver the click well after touchend

  if (!window.matchMedia) return;

  // Live, not captured once: a tablet with a keyboard case gains a fine
  // pointer mid-session, and the CSS gate flips at the same moment this does.
  var FINE = window.matchMedia('(hover: hover) and (pointer: fine)');
  function fine() {
    return FINE.matches;
  }

  var CHEVRON =
    '<svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false">' +
    '<path d="M15 4 L7 12 L15 20" fill="none" stroke="currentColor" stroke-width="1.6"' +
    ' stroke-linecap="round" stroke-linejoin="round"/></svg>';

  var cards = []; // every wired card, for the visibilitychange/pageshow sweep

  /* -------------------------------------------------------------- one card */

  function wire(img) {
    // An expando rather than a data attribute, deliberately. Attributes survive
    // cloneNode and listeners do not, so a card copied into the page rather
    // than re-fetched would arrive looking bound while being completely inert.
    // A property is per-object and cannot lie about that.
    if (img._instyleGalleryBound) return;

    var shots = (img.dataset.instyleGallery || '')
      .split('|')
      .map(function (s) {
        return s.trim();
      })
      .filter(function (s, i, all) {
        // Defensive dedupe. The Liquid emits the featured image first and then
        // skips it in the loop, but a theme edit that regresses that would
        // otherwise show the same picture twice and look like a broken swipe.
        return s && all.indexOf(s) === i;
      })
      .slice(0, MAX_SHOTS);

    // One image is just a picture. No gesture, no dots, no promise we cannot keep.
    if (shots.length < 2) return;

    var media = img.closest('.card__media');
    var wrapper = img.closest('.card-wrapper');
    if (!media || !wrapper) return;

    img._instyleGalleryBound = true;

    // Same clone problem from the other end: a copied card brings a dead copy
    // of the gallery DOM with it. Clear it before building the live one.
    var stale = media.querySelector('.instyle-cardgal');
    if (stale) stale.remove();

    /* ---- the layers and the affordance ---- */

    // Built as a sibling of `.media`, not inside it. Dawn owns the rules for
    // `.media > img` — including `media--hover-effect`, which flips opacity on
    // hover for any second <img> it finds — and the cleanest way not to fight
    // them is to keep our images out of their reach. It also leaves Dawn's
    // original <img> as `:only-child`, so its hover zoom keeps working.
    var stack = document.createElement('div');
    stack.className = 'instyle-cardgal';
    // The layers duplicate a picture the original <img> already describes, and
    // the controls are pointer decoration, so none of this is worth announcing.
    stack.setAttribute('aria-hidden', 'true');

    var L1 = document.createElement('img');
    var L2 = document.createElement('img');
    [L1, L2].forEach(function (el) {
      el.className = 'instyle-cardgal__layer';
      el.alt = '';
      el.decoding = 'async';
      el.draggable = false; // a mouse drag on a card should never peel an image off it
      stack.appendChild(el);
    });

    var ui = document.createElement('div');
    ui.className = 'instyle-cardgal__ui';

    var prev = document.createElement('span');
    prev.className = 'instyle-cardgal__arrow instyle-cardgal__arrow--prev';
    prev.innerHTML = CHEVRON;

    var next = document.createElement('span');
    next.className = 'instyle-cardgal__arrow instyle-cardgal__arrow--next';
    next.innerHTML = CHEVRON;

    var dotsBox = document.createElement('div');
    dotsBox.className = 'instyle-cardgal__dots';
    var dots = shots.map(function () {
      var d = document.createElement('span');
      d.className = 'instyle-cardgal__dot';
      dotsBox.appendChild(d);
      return d;
    });

    ui.appendChild(prev);
    ui.appendChild(next);
    ui.appendChild(dotsBox);
    stack.appendChild(ui);
    media.appendChild(stack);

    // Carries `touch-action` and scopes every gallery rule, so a single-image
    // card is left exactly as Dawn rendered it.
    wrapper.classList.add('instyle-cardgal-card');

    /* ---- state ---- */

    var index = 0; // what the visitor has asked for
    var shown = 0; // what is actually painted; they differ while a load is in flight
    var front = 0; // 0 = the original <img>, 1 = L1, 2 = L2
    var busy = false;
    var queued = null;
    var fadeTimer = null;
    var loadTimer = null;
    var warmed = false;
    var drag = null;
    var suppressUntil = 0;

    paintDots();

    /* ---- images ---- */

    // Deliberately not on page load: that would be every card's whole gallery
    // downloaded before anyone had scrolled to it, on a phone. First contact —
    // the finger landing, or the pointer entering — buys a few hundred
    // milliseconds of head start before the gesture can possibly complete, and
    // each committed step then warms its own neighbours, so only the very first
    // swipe on a cold connection can arrive before its picture does.
    function warm() {
      if (warmed) return;
      warmed = true;
      shots.forEach(function (src) {
        new Image().src = src;
      });
    }

    function preload(n) {
      var s = shots[((n % shots.length) + shots.length) % shots.length];
      if (s) new Image().src = s;
    }

    function loadInto(el, src, done) {
      if (el._instyleRelease) el._instyleRelease();

      if (el.getAttribute('src') === src && el.complete && el.naturalWidth) {
        done();
        return;
      }

      var fired = false;
      var t = 0;
      function go() {
        if (fired) return;
        fired = true;
        release();
        done();
      }
      function release() {
        clearTimeout(t);
        if (loadTimer === t) loadTimer = null;
        el.removeEventListener('load', go);
        el.removeEventListener('error', go);
        el._instyleRelease = null;
      }
      el._instyleRelease = release;

      el.addEventListener('load', go);
      el.addEventListener('error', go);
      // Rule 5. `load` is not delivered on a backgrounded tab, and a decode
      // that never finishes must not strand the card on the wrong picture.
      t = setTimeout(go, LOAD_CAP_MS);
      loadTimer = t;
      el.setAttribute('src', src);
      // A memory-cache hit can complete before the listener above is useful.
      if (el.complete && el.naturalWidth) go();
    }

    function setL2(value, instant) {
      if (instant) {
        L2.style.transition = 'none';
        L2.style.opacity = String(value);
        void L2.offsetWidth; // flush, so the next change animates again
        L2.style.transition = '';
      } else {
        L2.style.opacity = String(value);
      }
    }

    function run(n, instant) {
      busy = true;
      // L2 is the only element whose opacity moves. Which way it moves depends
      // on which buffer is currently in front — see the header comment.
      var toL1 = front === 2;
      var incoming = toL1 ? L1 : L2;

      loadInto(incoming, shots[n], function () {
        if (toL1) {
          // L2 still covers L1 completely, so making L1 opaque is invisible.
          L1.style.opacity = '1';
        }
        setL2(toL1 ? 0 : 1, instant);
        front = toL1 ? 1 : 2;
        shown = n;

        clearTimeout(fadeTimer);
        // A timer rather than `transitionend`, for the same reason as rule 5:
        // a hidden tab never delivers the event and the card would jam.
        fadeTimer = setTimeout(
          function () {
            busy = false;
            var q = queued;
            queued = null;
            if (q !== null && q !== shown) run(q, false);
          },
          instant ? 0 : FADE_MS + 20
        );
      });
    }

    function go(n) {
      n = ((n % shots.length) + shots.length) % shots.length;
      if (n === index) return;
      index = n;
      paintDots();
      preload(index + 1);
      preload(index - 1);
      if (busy) {
        queued = n; // only the latest target survives; intermediate frames are noise
        return;
      }
      run(n, false);
    }

    function paintDots() {
      for (var i = 0; i < dots.length; i++) {
        dots[i].classList.toggle('instyle-cardgal__dot--on', i === index);
      }
    }

    /* ---- what a click landed on ---- */

    function inside(el, x, y, pad) {
      if (!el || !el.offsetParent) return false; // display:none — not offered on this device
      var r = el.getBoundingClientRect();
      if (!r.width || !r.height) return false;
      pad = pad || 0;
      return x >= r.left - pad && x <= r.right + pad && y >= r.top - pad && y <= r.bottom + pad;
    }

    function controlAt(x, y) {
      if (inside(prev, x, y, 4)) return -1;
      if (inside(next, x, y, 4)) return 1;
      // Dots are inert under a finger. On touch they are the *indicator* that
      // a swipe exists; making a 6px target also tappable would only carve a
      // dead strip out of the tap-to-open-product area to offer a worse way of
      // doing what the swipe already does.
      if (fine()) {
        for (var i = 0; i < dots.length; i++) {
          if (inside(dots[i], x, y, 6)) return { to: i };
        }
      }
      return 0;
    }

    /* ---- gestures ---- */

    function onDown(e) {
      suppressUntil = 0; // a fresh contact always re-enables the link
      drag = null;
      if (!e.isPrimary) return;
      // A mouse drives the arrows; dragging one across a product grid is not a
      // gesture anyone makes, and claiming it would break text selection and
      // the browser's own image-drag.
      if (e.pointerType === 'mouse') return;
      var r = media.getBoundingClientRect();
      if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) return;
      warm();
      drag = {
        id: e.pointerId,
        x0: e.clientX,
        y0: e.clientY,
        anchor: e.clientX,
        axis: 0,
        moved: false,
        committed: false,
        reach: 0, // furthest the finger got from where it landed, horizontally
        width: r.width,
        samples: [[e.timeStamp, e.clientX]]
      };
    }

    function hot(el, on) {
      // Only touch the class when it actually changes: a style mutation
      // followed by the next move's getBoundingClientRect is a forced reflow,
      // and this runs on every mousemove over the card.
      if (el.classList.contains('instyle-cardgal__arrow--hot') !== on) {
        el.classList.toggle('instyle-cardgal__arrow--hot', on);
      }
    }

    function onMove(e) {
      if (e.pointerType === 'mouse') {
        // Synthetic hover state. The arrows never receive pointer events (see
        // the header), so :hover cannot do this for us.
        hot(prev, inside(prev, e.clientX, e.clientY, 4));
        hot(next, inside(next, e.clientX, e.clientY, 4));
        return;
      }
      if (!drag || e.pointerId !== drag.id) return;

      var dx = e.clientX - drag.x0;
      var dy = e.clientY - drag.y0;

      if (!drag.axis) {
        if (Math.abs(dx) < AXIS_SLOP && Math.abs(dy) < AXIS_SLOP) return;
        // Vertical wins ties. Missing a swipe is a shrug; stealing a scroll is a bug.
        if (Math.abs(dx) <= Math.abs(dy)) {
          drag = null;
          return;
        }
        drag.axis = 1;
      }

      drag.moved = true;
      drag.reach = Math.max(drag.reach, Math.abs(dx));
      drag.samples.push([e.timeStamp, e.clientX]);
      while (drag.samples.length > 2 && e.timeStamp - drag.samples[1][0] > FLICK_WINDOW_MS) {
        drag.samples.shift();
      }

      // The anchor is re-armed at every commit, so one long drag across the
      // card can walk through several images instead of dying after the first.
      var step = Math.max(COMMIT_MIN, Math.min(COMMIT_MAX, drag.width * COMMIT_FRACTION));
      var travel = e.clientX - drag.anchor;
      while (Math.abs(travel) >= step) {
        go(index + (travel < 0 ? 1 : -1)); // pushing the picture left brings the next one in
        drag.anchor += travel < 0 ? -step : step;
        drag.committed = true;
        travel = e.clientX - drag.anchor;
      }
    }

    function onUp(e, cancelled) {
      if (!drag || e.pointerId !== drag.id) return;
      var d = drag;
      drag = null;
      if (!d.moved) return;

      var acted = d.committed;

      if (!acted && !cancelled) {
        // Velocity at release over the last ~120ms, the same measure the flick
        // hero uses — it is what "how hard did you flick it" means to a person,
        // and a slow drag ending in a throw reads as a throw. There is no
        // duration to derive here, so rule 1's K/sqrt(v) does not apply; this
        // is only the threshold that lets a short, fast swipe still count.
        var first = d.samples[0];
        var last = d.samples[d.samples.length - 1];
        var dt = last[0] - first[0];
        var v = dt > 0 ? (last[1] - first[1]) / dt : 0;
        if (Math.abs(v) >= FLICK_PX_PER_MS) {
          go(index + (v < 0 ? 1 : -1));
          acted = true;
        }
      }

      // It did something, or it travelled far enough to have been meant, so it
      // was not a tap — whatever the browser decides to dispatch next. Defence
      // 3 from the header comment. A shorter wobble falls through and keeps
      // the link, because most taps on a phone wobble.
      if (acted || d.reach >= TAP_SLOP) suppressUntil = Date.now() + CLICK_GUARD_MS;
    }

    function onClick(e) {
      // `detail === 0` is a keyboard or scripted activation of the link, which
      // has no coordinates — (0, 0) must never be tested against a control's
      // rectangle, or a card sitting at the top-left of the viewport would eat
      // its own Enter key.
      if (!e.detail) return;
      if (Date.now() < suppressUntil) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      var hit = controlAt(e.clientX, e.clientY);
      if (!hit) return; // not ours — let the card link have it
      e.preventDefault();
      e.stopPropagation();
      warm();
      go(typeof hit === 'object' ? hit.to : index + hit);
    }

    function onKey(e) {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
      // Collection pages render quick-add inside the card. Arrow keys belong to
      // a field or a menu the moment one of them has focus.
      var t = e.target;
      if (t && (t.isContentEditable || /^(INPUT|SELECT|TEXTAREA)$/.test(t.tagName))) return;
      // Left/Right only ever scroll horizontally, and this page does not, so
      // claiming them while a card link has focus costs the visitor nothing —
      // and it means the gallery works from the keyboard without adding five
      // more tab stops to every one of the cards in the grid.
      e.preventDefault();
      warm();
      go(index + (e.key === 'ArrowRight' ? 1 : -1));
    }

    // Passive: this file never calls preventDefault on a touch, so the browser
    // is free to scroll the page the instant it wants to.
    wrapper.addEventListener('pointerdown', onDown, { passive: true, capture: true });
    wrapper.addEventListener('pointermove', onMove, { passive: true, capture: true });
    wrapper.addEventListener(
      'pointerup',
      function (e) {
        onUp(e, false);
      },
      { passive: true, capture: true }
    );
    wrapper.addEventListener(
      'pointercancel',
      function (e) {
        onUp(e, true);
      },
      { passive: true, capture: true }
    );
    // Capture, and NOT passive: this one has to be able to cancel the link.
    wrapper.addEventListener('click', onClick, true);
    wrapper.addEventListener('keydown', onKey);
    // Hover intent, mouse only. Nothing visible happens — it just means the
    // arrows have something to show the instant one is clicked.
    wrapper.addEventListener('pointerenter', function (e) {
      if (e.pointerType === 'mouse') warm();
    });

    // Read the DOM rather than the bookkeeping. Rule 6's last step is "verify
    // and re-seek if it never landed", and a recovery that only compares its
    // own two variables cannot tell the difference between a swap that landed
    // and one that was dropped after the variables were already updated.
    function painted() {
      if (shown !== index) return false;
      if (front === 0) return index === 0;
      var el = front === 1 ? L1 : L2;
      if (el.getAttribute('src') !== shots[index]) return false;
      return front === 2 ? L2.style.opacity === '1' : L2.style.opacity === '0' && L1.style.opacity === '1';
    }

    cards.push({
      el: wrapper,
      // Rule 6. Coming back to the tab, throw away anything half-finished and
      // re-assert the frame this card believes it is on. `load` and the fade
      // timer both go missing while hidden, so what is believed and what is
      // shown can have drifted apart, and neither would ever correct itself.
      settle: function () {
        drag = null;
        suppressUntil = 0;
        clearTimeout(fadeTimer);
        clearTimeout(loadTimer);
        if (L1._instyleRelease) L1._instyleRelease();
        if (L2._instyleRelease) L2._instyleRelease();
        busy = false;
        queued = null;
        paintDots();
        // Re-running loads into the *other* buffer and reveals that, so a
        // buffer left in a bad state is stepped off rather than repaired in
        // place — the same "never seek to the frame you are already on" move
        // the hero recovery makes.
        if (!painted()) run(index, true);
      },
      drop: function () {
        drag = null;
        suppressUntil = 0;
      }
    });
  }

  /* ------------------------------------------------------------- the page */

  function scan(root) {
    (root || document).querySelectorAll('img[data-instyle-gallery]').forEach(wire);
  }

  function sweep(settle) {
    for (var i = cards.length - 1; i >= 0; i--) {
      // Filtering and pagination replace whole grids; without this the registry
      // would grow for the life of the session.
      if (!cards[i].el.isConnected) {
        cards.splice(i, 1);
        continue;
      }
      if (settle) cards[i].settle();
      else cards[i].drop();
    }
  }

  document.addEventListener('visibilitychange', function () {
    sweep(!document.hidden);
  });

  // Separately from visibilitychange: a bfcache restore fires neither that nor
  // `load`, and comes back with listeners attached but state possibly stale.
  window.addEventListener('pageshow', function () {
    scan();
    sweep(true);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      scan();
    });
  } else {
    scan();
  }

  // Filtering, pagination and section re-renders replace the grid without a
  // page load, so newly injected cards have to be picked up too.
  if ('MutationObserver' in window) {
    new MutationObserver(function (records) {
      records.forEach(function (r) {
        r.addedNodes.forEach(function (n) {
          if (n.nodeType !== 1) return;
          if (n.matches && n.matches('img[data-instyle-gallery]')) wire(n);
          else scan(n);
        });
      });
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
